import type { Currency, Tier } from "@mw/shared/pricing";
import type { Locale } from "@mw/shared/i18n";
import { getPriceForCurrency } from "@mw/shared/pricing";
import { formatPrice } from "@utilities/numbers";
import type { StaticImageData } from "next/image";

import FreeCover from "./covers/Free.png";
import StarterCover from "./covers/Starter.png";
import CreatorCover from "./covers/Creator.png";
import ProCover from "./covers/Pro.png";
import BusinessCover from "./covers/Business.png";
import ScaleCover from "./covers/Scale.png";
import EnterpriseCover from "./covers/Enterprise.png";

/**
 * Returns the effective tier for a calculator result card, applying the starter
 * monthly promo when enabled. The first-month discount is monthly-only, so the
 * promo is skipped for yearly billing. applyStarterPromotion guards planId internally.
 */
export function getCalculatorDisplayTier(tier: Tier, period: "monthly" | "yearly", showStarterPromo?: boolean): Tier {
  return showStarterPromo && period === "monthly" ? applyStarterPromotion(tier) : tier;
}

/** Returns a new Tier with the starter monthly promo applied ($1 first month). */
export function applyStarterPromotion(tier: Tier): Tier {
  if (tier.planId !== "starter" || !tier.pricing.subscription?.monthly) return tier;
  return {
    ...tier,
    pricing: {
      ...tier.pricing,
      subscription: {
        ...tier.pricing.subscription,
        monthly: {
          ...tier.pricing.subscription.monthly,
          discountedPrice: 1,
          discountMessageKey: "first_month_one_dollar" as const,
        },
      },
    },
  };
}

export function setStarterTierPromotion(tiers: Tier[]): Tier[] {
  return tiers.map(t => applyStarterPromotion(t));
}

export function getPrices(pricing: Tier["pricing"], period: "monthly" | "yearly") {
  const defaultPricing = { price: undefined, discountedPrice: undefined, discountMessageKey: undefined };
  const subscription = pricing?.subscription;
  if (period === "yearly" && subscription?.yearly) {
    return { ...defaultPricing, ...subscription.yearly };
  }
  if (period === "monthly" && subscription?.monthly) {
    return { ...defaultPricing, ...subscription.monthly };
  }
  return defaultPricing;
}

export function getFormattedPrices(
  tier: Tier["pricing"],
  period: "monthly" | "yearly",
  currency: Currency,
  currencySymbol: string,
  locale: Locale,
) {
  const { price, discountedPrice, discountMessageKey } = getPrices(tier, period);
  const displayedPrice = discountedPrice ?? price ?? undefined;
  const convertedDisplayedPrice =
    displayedPrice !== undefined ? getPriceForCurrency(displayedPrice, currency) : undefined;
  const convertedOriginalPrice = price !== undefined ? getPriceForCurrency(price, currency) : undefined;
  const currencyFormattedPrice =
    convertedDisplayedPrice !== undefined ? formatPrice(convertedDisplayedPrice, currencySymbol, locale) : "";
  const formattedOriginalPrice =
    convertedOriginalPrice !== undefined ? formatPrice(convertedOriginalPrice, currencySymbol, locale) : "";

  return {
    price,
    discountedPrice,
    discountMessageKey,
    convertedOriginalPrice,
    currencyFormattedPrice,
    formattedOriginalPrice,
  };
}

export const TIER_COVER_IMAGES: Record<string, StaticImageData> = {
  free: FreeCover,
  starter: StarterCover,
  creator: CreatorCover,
  pro: ProCover,
  business: BusinessCover,
  scale: ScaleCover,
  scale_2024_08_10: BusinessCover,
  enterprise: EnterpriseCover,
  payg: FreeCover,
};
